/** Feature data channels, each versioned independently (docs/PRD.md §6–7). */
export const CHANNELS = ["doc", "aware", "chat", "term", "blob"] as const;
export type ChannelName = (typeof CHANNELS)[number];

/** Inclusive [min, max] supported version range. */
export type VersionRange = readonly [min: number, max: number];

/** Channel protocol versions this build speaks. Bump max (and keep min) to stay compatible. */
export const PROTOCOLS: Record<ChannelName, VersionRange> = {
  doc: [1, 1],
  aware: [1, 1],
  chat: [1, 1],
  term: [1, 1],
  blob: [1, 1],
};

/** Shared-state schema versions (the Y.Doc layout). */
export const SCHEMAS = { doc: [1, 1] } as const satisfies Record<string, VersionRange>;
export type SchemaName = keyof typeof SCHEMAS;

/** Highest version both ranges support, or null if they don't overlap. */
export function negotiateRange(
  local: VersionRange,
  remote: VersionRange | undefined,
): number | null {
  if (!remote) return null;
  const high = Math.min(local[1], remote[1]);
  const low = Math.max(local[0], remote[0]);
  return high >= low ? high : null;
}

/**
 * Picks a version per key that both peers support. Keys the remote doesn't list (e.g. a feature
 * it predates) negotiate to null, which disables that feature for that peer.
 */
export function negotiate<K extends string>(
  local: Readonly<Record<K, VersionRange>>,
  remote: Readonly<Partial<Record<string, VersionRange>>>,
): Record<K, number | null> {
  const result = {} as Record<K, number | null>;
  for (const key of Object.keys(local) as K[]) {
    result[key] = negotiateRange(local[key], remote[key]);
  }
  return result;
}

const CHANNEL_PROTOCOL = /^cs\.([a-z][a-z0-9-]*)\/([1-9]\d*)$/;

/** RTCDataChannel `protocol` value for a channel version: ("doc", 1) → "cs.doc/1". */
export function channelProtocol(name: string, version: number): string {
  return `cs.${name}/${version}`;
}

export function parseChannelProtocol(
  protocol: string,
): { name: string; version: number } | undefined {
  const match = CHANNEL_PROTOCOL.exec(protocol);
  if (!match?.[1] || !match[2]) return undefined;
  return { name: match[1], version: Number(match[2]) };
}
